/**
 * Catalogo moduli di specializzazione + helpers prezzo.
 * Single source of truth condivisa tra UI billing, marketing, server (chat).
 */

export type ModuleTier = "standard" | "premium" | "beta";
export type BillingCycle = "monthly" | "annual";
export type BasePlanId = "starter" | "pro" | "studio";

export type SpecializationModule = {
  id: string;
  name: string;
  short: string;
  tier: ModuleTier;
  monthlyPrice: number;
  priceIdMonthly: string;
  priceIdAnnual: string;
  sources: string[];
};

/** I 15 ambiti del Foro italiano supportati dall'app. */
export const MODULES: SpecializationModule[] = [
  { id: "privacy", name: "Privacy", short: "GDPR, Garante, EDPB", tier: "premium", monthlyPrice: 29,
    priceIdMonthly: "module_privacy_monthly", priceIdAnnual: "module_privacy_annual",
    sources: ["Reg. UE 2016/679", "D.Lgs. 196/2003", "Provv. Garante", "Linee guida EDPB"] },
  { id: "lavoro", name: "Lavoro", short: "CCNL, Cassazione lavoro", tier: "premium", monthlyPrice: 29,
    priceIdMonthly: "module_lavoro_monthly", priceIdAnnual: "module_lavoro_annual",
    sources: ["Statuto dei lavoratori", "Codice civile (Libro V)", "CCNL", "Cass. Sez. Lavoro"] },
  { id: "tributario", name: "Tributario", short: "AdE, Cass. Tributaria", tier: "premium", monthlyPrice: 29,
    priceIdMonthly: "module_tributario_monthly", priceIdAnnual: "module_tributario_annual",
    sources: ["DPR 600/1973", "DPR 633/1972", "Circ. Agenzia Entrate", "Cass. Sez. Trib."] },
  { id: "bancario", name: "Bancario e Assicurativo", short: "TUB, IVASS, ABF", tier: "premium", monthlyPrice: 29,
    priceIdMonthly: "module_bancario_monthly", priceIdAnnual: "module_bancario_annual",
    sources: ["TUB D.Lgs. 385/1993", "TUF D.Lgs. 58/1998", "Cod. Ass. D.Lgs. 209/2005", "ABF/IVASS"] },
  { id: "societario", name: "Societario", short: "CCII, registro imprese", tier: "premium", monthlyPrice: 29,
    priceIdMonthly: "module_societario_monthly", priceIdAnnual: "module_societario_annual",
    sources: ["Codice civile (Libro V)", "CCII D.Lgs. 14/2019", "Reg. CONSOB"] },
  { id: "crisi", name: "Crisi d'Impresa", short: "CCII, concordati", tier: "premium", monthlyPrice: 39,
    priceIdMonthly: "module_crisi_monthly", priceIdAnnual: "module_crisi_annual",
    sources: ["CCII D.Lgs. 14/2019", "Linee guida CNDCEC", "Cass. Sez. I civile"] },
  { id: "compliance", name: "Compliance", short: "231, AML, ESG", tier: "premium", monthlyPrice: 39,
    priceIdMonthly: "module_compliance_monthly", priceIdAnnual: "module_compliance_annual",
    sources: ["D.Lgs. 231/2001", "D.Lgs. 231/2007 (AML)", "Reg. UE 2020/852 (Tassonomia)", "Linee guida CNDCEC"] },
  { id: "civile", name: "Civile", short: "Cass. civile, contratti", tier: "standard", monthlyPrice: 19,
    priceIdMonthly: "module_civile_monthly", priceIdAnnual: "module_civile_annual",
    sources: ["Codice civile", "Codice di procedura civile", "Cass. Sez. Civili"] },
  { id: "famiglia", name: "Famiglia", short: "Separazioni, minori", tier: "standard", monthlyPrice: 19,
    priceIdMonthly: "module_famiglia_monthly", priceIdAnnual: "module_famiglia_annual",
    sources: ["Codice civile (Libro I)", "L. 898/1970 divorzio", "Riforma Cartabia", "Cass. Sez. I"] },
  { id: "amministrativo", name: "Amministrativo", short: "TAR, CdS, appalti", tier: "standard", monthlyPrice: 19,
    priceIdMonthly: "module_amministrativo_monthly", priceIdAnnual: "module_amministrativo_annual",
    sources: ["L. 241/1990", "Cod. Appalti D.Lgs. 36/2023", "C.P.A.", "Cons. Stato e TAR"] },
  { id: "penale", name: "Penale", short: "Cass. penale, 231", tier: "standard", monthlyPrice: 19,
    priceIdMonthly: "module_penale_monthly", priceIdAnnual: "module_penale_annual",
    sources: ["Codice penale", "Codice di procedura penale", "Cass. Sez. Penali", "D.Lgs. 231/2001"] },
  { id: "real-estate", name: "Real Estate", short: "Locazioni, urbanistica", tier: "standard", monthlyPrice: 19,
    priceIdMonthly: "module_real_estate_monthly", priceIdAnnual: "module_real_estate_annual",
    sources: ["L. 392/1978", "L. 431/1998", "TU Edilizia DPR 380/2001", "Cass. Sez. III"] },
  { id: "ip", name: "Proprietà Intellettuale", short: "UIBM, EUIPO, marchi", tier: "standard", monthlyPrice: 19,
    priceIdMonthly: "module_ip_monthly", priceIdAnnual: "module_ip_annual",
    sources: ["CPI D.Lgs. 30/2005", "L. 633/1941 (LDA)", "EUIPO/UIBM", "Cass. Sez. I"] },
  { id: "consumatori", name: "Diritto dei Consumatori", short: "Cod. Consumo, ADR", tier: "standard", monthlyPrice: 19,
    priceIdMonthly: "module_consumatori_monthly", priceIdAnnual: "module_consumatori_annual",
    sources: ["Codice del Consumo D.Lgs. 206/2005", "Provv. AGCM", "ADR Conciliaweb"] },
  { id: "scolastico", name: "Diritto Scolastico", short: "TU scuola — Beta", tier: "beta", monthlyPrice: 9,
    priceIdMonthly: "module_scolastico_monthly", priceIdAnnual: "module_scolastico_annual",
    sources: ["D.Lgs. 297/1994", "L. 107/2015", "Sentenze TAR scuola"] },
];

export type BasePlan = {
  id: BasePlanId;
  name: string;
  monthlyPrice: number;
  /** Quanti moduli inclusi gratis (Standard tier). 0 = nessuno. -1 = tutti illimitati. */
  includedStandardModules: number;
  aiQueriesPerMonth: number;
};

export const BASE_PLANS: BasePlan[] = [
  { id: "starter", name: "Starter", monthlyPrice: 0, includedStandardModules: 0, aiQueriesPerMonth: 100 },
  { id: "pro", name: "Pro", monthlyPrice: 49, includedStandardModules: 3, aiQueriesPerMonth: 1_000 },
  { id: "studio", name: "Studio", monthlyPrice: 149, includedStandardModules: -1, aiQueriesPerMonth: 10_000 },
];

export type PriceBreakdown = {
  basePrice: number;
  modulesPrice: number;
  includedDiscount: number;
  bundleDiscount: number;
  bundleDiscountPct: number;
  cycleDiscount: number;
  cycleDiscountPct: number;
  subtotal: number;
  total: number;
  totalAnnualEquivalent: number;
  cycle: BillingCycle;
  selectedModules: SpecializationModule[];
};

/**
 * Calcola il prezzo totale a partire da:
 * - piano base
 * - moduli scelti
 * - ciclo (monthly / annual)
 *
 * Sconti:
 * - 3+ moduli pagati: −15% sui moduli
 * - 5+ moduli pagati: −25% sui moduli
 * - Annuale: ulteriore −20% sul subtotale (piano + moduli)
 * - Pro: i primi N moduli **Standard** (in ordine di selezione) sono inclusi
 * - Studio: tutti i moduli inclusi
 */
export function computePrice(
  baseId: BasePlanId,
  moduleIds: string[],
  cycle: BillingCycle = "monthly",
): PriceBreakdown {
  const base = BASE_PLANS.find((p) => p.id === baseId) ?? BASE_PLANS[0];
  const selected = MODULES.filter((m) => moduleIds.includes(m.id));

  // Studio = tutti inclusi
  if (base.includedStandardModules === -1) {
    const subtotal = base.monthlyPrice;
    const cyclePct = cycle === "annual" ? 0.2 : 0;
    const total = round2(subtotal * (1 - cyclePct));
    return {
      basePrice: base.monthlyPrice,
      modulesPrice: 0,
      includedDiscount: selected.reduce((s, m) => s + m.monthlyPrice, 0),
      bundleDiscount: 0,
      bundleDiscountPct: 0,
      cycleDiscount: round2(subtotal * cyclePct),
      cycleDiscountPct: cyclePct * 100,
      subtotal,
      total,
      totalAnnualEquivalent: round2(total * 12),
      cycle,
      selectedModules: selected,
    };
  }

  // Pro: scala N moduli Standard come "inclusi" (ordine: prima quelli scelti, dando priorità agli Standard)
  const standardSelected = selected.filter((m) => m.tier === "standard");
  const includedCount = Math.min(base.includedStandardModules, standardSelected.length);
  const includedIds = new Set(standardSelected.slice(0, includedCount).map((m) => m.id));
  const includedDiscount = standardSelected
    .slice(0, includedCount)
    .reduce((s, m) => s + m.monthlyPrice, 0);

  const paidModules = selected.filter((m) => !includedIds.has(m.id));
  const modulesGross = paidModules.reduce((s, m) => s + m.monthlyPrice, 0);

  // Bundle discount sui moduli pagati
  let bundlePct = 0;
  if (paidModules.length >= 5) bundlePct = 0.25;
  else if (paidModules.length >= 3) bundlePct = 0.15;
  const bundleDiscount = round2(modulesGross * bundlePct);

  const subtotal = round2(base.monthlyPrice + modulesGross - bundleDiscount);

  // Sconto annuale sul subtotale
  const cyclePct = cycle === "annual" ? 0.2 : 0;
  const cycleDiscount = round2(subtotal * cyclePct);
  const total = round2(subtotal - cycleDiscount);

  return {
    basePrice: base.monthlyPrice,
    modulesPrice: modulesGross,
    includedDiscount,
    bundleDiscount,
    bundleDiscountPct: bundlePct * 100,
    cycleDiscount,
    cycleDiscountPct: cyclePct * 100,
    subtotal,
    total,
    totalAnnualEquivalent: round2(total * 12),
    cycle,
    selectedModules: selected,
  };
}

function round2(n: number) {
  return Math.round(n * 100) / 100;
}

/** Map area name (UI label) → module id. */
export function moduleIdFromArea(area: string | null | undefined): string | null {
  if (!area) return null;
  const norm = area.trim().toLowerCase();
  const direct = MODULES.find((m) => m.name.toLowerCase() === norm);
  if (direct) return direct.id;
  // fallback contains
  const partial = MODULES.find((m) => norm.includes(m.id) || norm.includes(m.name.toLowerCase()));
  return partial?.id ?? null;
}

export function getModule(id: string): SpecializationModule | undefined {
  return MODULES.find((m) => m.id === id);
}
